"use client";

import { useState, type RefObject } from "react";

import { CheckIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { filterOptionsBySearch } from "@/components/ui/filter-options-by-search";
import { MultiFilterSelectTrigger } from "@/components/ui/multi-filter-select-trigger";
import { multiFilterSelectedLabel } from "@/components/ui/multi-filter-selected-label";
import { selectSurfaceLayout } from "@/components/ui/select-surface-layout";
import { useFormatNumber } from "@/hooks/use-format-number";
import { cn } from "@/lib/utils";

import type { FilterOption } from "./filter-select";
import { SearchableSelectSurface } from "./searchable-select-surface";

interface MultiFilterSelectProps {
  icon: AppIcon;
  placeholder: string;
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  searchable?: boolean;
  filterSearchOptions?: boolean;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  variant?: "compact" | "fullWidth";
  showTriggerIcon?: boolean;
}

export function MultiFilterSelect({
  icon,
  placeholder,
  options,
  value,
  onChange,
  searchable,
  filterSearchOptions = true,
  onSearchChange,
  searchPlaceholder,
  variant = "compact",
  showTriggerIcon = true,
}: MultiFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const formatNumber = useFormatNumber();
  const isFullWidth = variant === "fullWidth";

  const selectedCount = value.length;
  const selectedValues = new Set(value);
  const selectedLabel = multiFilterSelectedLabel(options, value);
  const surfaceLayout = selectSurfaceLayout(
    "w-[min(22rem,calc(100vw-2rem))] bg-card rounded-md",
    "w-[240px]",
    isFullWidth,
  );

  const setOpenState = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearch("");
      onSearchChange?.("");
    }
  };

  const filteredOptions = filterOptionsBySearch(options, searchable && filterSearchOptions, search);
  const displayedOptions = [...filteredOptions].sort((a, b) => {
    const aSelected = selectedValues.has(a.value);
    const bSelected = selectedValues.has(b.value);
    if (aSelected === bSelected) return 0;
    return aSelected ? -1 : 1;
  });

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const active = selectedCount > 0;

  const renderOptionList = (className: string) => (
    <div className={cn("overflow-y-auto overscroll-contain flex flex-col", className)}>
      {displayedOptions.map((opt) => {
        const isSelected = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            onClick={() => toggle(opt.value)}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors",
              isSelected
                ? "bg-primary/10 text-foreground"
                : "hover:bg-secondary/80 text-foreground",
            )}
          >
            <span
              className={cn(
                "shrink-0 w-4 h-4 rounded-sm border flex items-center justify-center transition-colors",
                isSelected ? "bg-primary border-primary" : "border-border bg-secondary",
              )}
            >
              {isSelected && (
                <CheckIcon className="text-primary-foreground text-[14px] leading-none" />
              )}
            </span>
            {opt.iconUrl && (
              <img src={opt.iconUrl} alt="" className="w-4 h-auto rounded-sm shrink-0" />
            )}
            <span className="flex-1 truncate">{opt.label}</span>
            {opt.count != null && (
              <span
                className={cn(
                  "text-xs tabular-nums px-1.5 py-0.5 rounded-full",
                  isSelected ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground",
                )}
              >
                {formatNumber(opt.count)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderSearchInput = (ref: RefObject<HTMLInputElement | null>) =>
    searchable ? (
      <div className="p-2 border-b border-border">
        <input
          ref={ref}
          type="text"
          value={search}
          autoFocus
          onChange={(e) => {
            setSearch(e.target.value);
            onSearchChange?.(e.target.value);
          }}
          placeholder={searchPlaceholder ?? placeholder}
          className="w-full max-sm:min-h-11 px-2.5 py-2 bg-secondary border border-border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
    ) : null;

  return (
    <SearchableSelectSurface
      open={open}
      onOpenChange={setOpenState}
      title={placeholder}
      icon={icon}
      shouldFocus={searchable}
      desktopPanelClassName={surfaceLayout.desktopPanelClassName}
      desktopOptionsClassName={surfaceLayout.desktopOptionsClassName}
      renderTrigger={({ open: isOpen, setOpen: setSurfaceOpen }) => (
        <MultiFilterSelectTrigger
          open={isOpen}
          setOpen={setSurfaceOpen}
          icon={icon}
          showTriggerIcon={showTriggerIcon}
          selectedLabel={selectedLabel}
          placeholder={placeholder}
          isFullWidth={isFullWidth}
          active={active}
          countLabel={`${formatNumber(selectedCount)} / ${formatNumber(options.length)}`}
          onClear={() => {
            onChange([]);
            setOpenState(false);
          }}
        />
      )}
      renderInput={renderSearchInput}
      renderOptions={(_, className) => renderOptionList(className)}
    />
  );
}
